import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Alert,
  InputAdornment,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as ContentCopyIcon,
  Casino as CasinoIcon,
  Save as SaveIcon,
  VpnKey as VpnKeyIcon,
} from '@mui/icons-material';
import { db } from '../firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

// Génère une clé lisible à l'oral/à taper (évite les caractères ambigus 0/O/1/I)
function generateKey(length = 8) {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

function TeacherRegistrationKey() {
  const [currentKey, setCurrentKey] = useState('');
  const [draftKey, setDraftKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [message, setMessage] = useState(null); // { severity, text }

  useEffect(() => {
    const fetchKey = async () => {
      setLoading(true);
      try {
        const snap = await getDoc(doc(db, 'settings', 'teacherRegistration'));
        const key = snap.exists() ? (snap.data().securityKey || '') : '';
        setCurrentKey(key);
        setDraftKey(key);
      } catch (e) {
        console.error('Erreur de chargement de la clé:', e);
        setMessage({ severity: 'error', text: 'Erreur lors du chargement de la clé.' });
      } finally {
        setLoading(false);
      }
    };
    fetchKey();
  }, []);

  const handleSave = async () => {
    if (!draftKey.trim()) {
      setMessage({ severity: 'error', text: 'La clé ne peut pas être vide.' });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      await setDoc(
        doc(db, 'settings', 'teacherRegistration'),
        { securityKey: draftKey.trim(), updatedAt: serverTimestamp() },
        { merge: true }
      );
      setCurrentKey(draftKey.trim());
      setMessage({ severity: 'success', text: 'Clé mise à jour avec succès.' });
    } catch (e) {
      console.error('Erreur sauvegarde clé:', e);
      setMessage({ severity: 'error', text: 'Erreur lors de la sauvegarde.' });
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = () => {
    setDraftKey(generateKey());
    setShowKey(true);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(currentKey);
      setMessage({ severity: 'success', text: 'Clé copiée dans le presse-papier.' });
    } catch {
      setMessage({ severity: 'error', text: 'Impossible de copier automatiquement.' });
    }
  };

  if (loading) {
    return (
      <Card sx={{ maxWidth: 480 }}>
        <CardContent>
          <Typography color="textSecondary">Chargement...</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ maxWidth: 480 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <VpnKeyIcon sx={{ color: '#7C3AED' }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Clé d'inscription professeur
          </Typography>
        </Box>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Cette clé est demandée aux professeurs quand ils créent leur compte sur l'app mobile.
          Partage-la uniquement aux personnes concernées.
        </Typography>

        {message && <Alert severity={message.severity} sx={{ mb: 2 }}>{message.text}</Alert>}

        <TextField
          fullWidth
          label="Clé actuelle"
          type={showKey ? 'text' : 'password'}
          value={draftKey}
          onChange={(e) => setDraftKey(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowKey((s) => !s)}>
                  {showKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
                <IconButton onClick={handleCopy} disabled={!currentKey}>
                  <ContentCopyIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<CasinoIcon />}
            onClick={handleGenerate}
          >
            Générer une clé
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving || draftKey === currentKey}
            sx={{ backgroundColor: '#7C3AED', '&:hover': { backgroundColor: '#6D28D9' } }}
          >
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

export default TeacherRegistrationKey;
